/* eslint-disable max-lines */
import { featureMap as georgeIdMigrationTourFeatures } from '@retail/sid-georgeid-migration-config';
import { featureMap as entitlements } from '@retail/entitlements-config';
import { featureMap as promotionsFeatures } from '@shared/promotions-config';
import { featureMap as instantCashFeatures } from '@retail/instant-cash-config';
import { moneyAutomationFeaturesMap } from '@retail/money-automation-config';
import { phoebeFlags as leasingConfig } from '@retail/leasing-config';
import { gatekeeperFlags as loanFeatures } from '@retail/loan-config';
import { phoebeFlags as pensionFeatures } from '@retail/pension-config';
import type { RetailFeatures } from '@retail/gatekeeper';
import {
    addressBookFeaturesMap,
    directDebitFeaturesMap,
    ownFxTransferFeaturesMap,
    ownTransferFeaturesMap,
    payFeaturesMap,
    paymentOrdersFeaturesMap,
    paymentsFeaturesMap,
    sepaDirectDebitFeaturesMap,
    standingOrdersFeaturesMap,
} from '@retail/payments-config';
import { featureMap as cashFlowFeatures } from '@retail/cash-flow-config';
import { cardReservationsFeatures, reservationsFeatures } from '@retail/reservations-config';
import { headerFeatureMap } from '@retail/navigation-config';
import { overviewFeatureMap } from '@retail/overview-config';
import { storeFeatureMap } from '@shared/store-config';
import { featureMap as investmentsFeatures } from '@retail/investments-config';
import { phoebeFlags as insuranceFeatures } from '@retail/insurance-config';
import {
    miscellaneousFeatures as miscellaneousTransactionsFeatures,
    features as transactionsFeatures,
    shorthandFeatures as transactionsShorthandFeatures,
} from '@retail/transactions-config';
import { newPfmFeaturesMap } from '@retail/pfm-config';
import { ProductsPhoebeFlag } from '@retail/products-config';
import { securitySettingsRetailFeatureMap } from '@shared/security-settings-config';
import {
    cardActivationFlowV2,
    cardCancellationWarning,
    cardClosingInfoScreen,
    cardsGatekeeper,
    creditLimitChange,
    newBlockReorderFunction,
    newCardLimitChange,
    repaymentMethodChange,
} from '@retail/cards-config';
import { featureMap as appointments } from '@retail/appointments-config';

export const featuresSK: RetailFeatures = {
    ...cardsGatekeeper.SK,
    cards: {
        ...cardReservationsFeatures.SK,
        cardActivationFlowV2: cardActivationFlowV2.SK,
        cardCancellationWarning: cardCancellationWarning.SK,
        cardClosingInfoScreen: cardClosingInfoScreen.SK,
        newCardLimitChange: newCardLimitChange.SK,
        newBlockReorderFunction: newBlockReorderFunction.SK,
        RepaymentMethodChange: repaymentMethodChange.SK,
        CreditLimitChange: creditLimitChange.SK,
    },
    ...ProductsPhoebeFlag.SK,
    ...insuranceFeatures.SK,
    ...leasingConfig.SK,
    ...loanFeatures.SK,
    ...pensionFeatures.SK,
    ...miscellaneousTransactionsFeatures.SK,
    transactions: transactionsFeatures.SK,
    trx: transactionsShorthandFeatures.SK,
    investments: investmentsFeatures.SK,
    inv: investmentsFeatures.SK,
    addressBook: addressBookFeaturesMap.SK,
    ownTransfer: ownTransferFeaturesMap.SK,
    ownFxTransfer: ownFxTransferFeaturesMap.SK,
    paymentOrders: paymentOrdersFeaturesMap.SK,
    payments: paymentsFeaturesMap.SK,
    pay: payFeaturesMap.SK,
    sepaDirectDebit: sepaDirectDebitFeaturesMap.SK,
    standingOrders: standingOrdersFeaturesMap.SK,
    aboutYou: {
        enabled: true,
        _description:
            'Place to view and edit personal information - education, employment etc. Accessible through the settings menu under the main navigation',
        taxInfo: {
            enabled: false,
            _description: 'Enter tax information details',
        },
    },
    header: headerFeatureMap.SK,
    overview: overviewFeatureMap.SK,
    promotions: promotionsFeatures.SK,
    instantCash: instantCashFeatures.SK,
    cashFlow: cashFlowFeatures.SK,
    securitySettings: securitySettingsRetailFeatureMap.SK,
    settings: {
        entitlements: entitlements.SK,
    },
    signing: {
        georgeIdMigrationTour: georgeIdMigrationTourFeatures.SK,
        designUpdate: { enabled: true, _description: 'Design update for signing' },
    },
    store: storeFeatureMap.SK,
    reservations: reservationsFeatures.SK,
    moneyAutomation: moneyAutomationFeaturesMap.SK,
    newPfm: newPfmFeaturesMap.SK,
    luxTracking: {
        enabled: false,
    },
    beanCounterTracking: { enabled: true },
    posthogTracking: { enabled: false },
    gatekeeper: { enabled: true },
    gdpr: {
        enabled: true,
        _description: 'GDPR consents management',
    },
    consents: {
        enabled: false,
        _description: 'New consents management, which support also consents versioning',
    },
    headerLogoAnimation: { enabled: false },
    balanceRefreshService: { enabled: true },
    longPollAfterPayment: { enabled: true },
    watchdogs: {
        enabled: false,
        _description:
            'Setup and manage personal watchdogs for products and transactions. Accessible through the settings dropdown menu in the main navigation',
        securities: {
            enabled: false,
        },
        filterForLegalEntities: {
            enabled: false,
            _description:
                'If feature is enabled, the watchdogs will be hidden from LEGAL_ENTITY customer segment. If the feature is disabled, watchdogs will be shown to all customer segments.',
        },
    },
    mustReadMessages: {
        enabled: true,
        _description:
            'Mandatory bank messages which are shown during George bootstrap and block user from entering application until they are not mark as read',
    },
    bankMessages: {
        enabled: true,
        _description:
            'General bank messages shown to user, contains read must read messages and information messages',
        markAllAsRead: {
            enabled: false,
            _description: 'Mark all unread bank messages as read',
        },
    },
    temporaryMessages: { enabled: true },
    messenger: {
        enabled: true,
        markAllAsRead: {
            enabled: false,
            _description: 'Mark all unread conversations as read',
        },
        personalAdvisory: {
            enabled: false,
            _description: 'Enables personal advisory for the user instead of "Expert advisor team"',
        },
    },
    consumerInformationDelivery: {
        enabled: false,
        _description: 'Enables Electronic Business Information on George',
    },
    contactDataManagement: {
        enabled: true,
        autocompleteAddressSearch: {
            enabled: true,
            _description: 'Search addresses and automatically prefill the form data on selection',
        },
        confirm: {
            enabled: false,
            _description: 'Requires confirmation that contact details are accurate',
        },
    },
    documentsAndRequests: {
        enabled: true,
        markAllAsRead: {
            enabled: false,
            _description:
                'Show a button to mark all Documents & Requests of type FOR_DISPLAY as read ',
        },
        filter: {
            enabled: false,
            _description: 'Enables Documents and requests filter',
        },
        detailRedesign: {
            enabled: false,
            _description: 'Enables Documents and requests detail redesign WIP view',
        },
    },
    appointments: appointments.SK,
    appointmentsBooking: {
        enabled: true,
        _description: 'Appointments with personal advisors. Located in the communication hub',
    },
    acsVideoCalls: {
        enabled: false,
        _description: 'Video call integration with Azure Communication Services solution',
    },
    heyGeorge: {
        enabled: false,
        ai: {
            enabled: false,
        },
    },
    gdsHelpMenu: {
        enabled: true,
        helpArticles: {
            enabled: false,
        },
    },
    sentry: {
        enabled: true,
        _description: 'Error tracking service',
    },
    accountReservations: {
        enabled: true,
    },
    notifications: {
        enabled: false,
        markAllAsRead: {
            enabled: false,
            _description:
                'Decides whether display the `Mark all unread notifications as read` button on Watchdog Notifications page',
        },
    },
    kycForm: {
        enabled: false,
        _description: 'KYC - Know Your Customer - questionnaire, can be found in user settings',
    },
    kycHandoff: {
        enabled: false,
        _description: 'KYC - Know Your Customer - questionnaire, opens external application',
    },
    realtimeBankInfo: {
        enabled: true,
        _description:
            'Custom SK implementation of personal watchdogs for products. Accessible in product settings',
    },
    directDebit: directDebitFeaturesMap.SK,
    bsExtendedGrid: {
        enabled: false,
    },
    startupPerformanceTracking: {
        enabled: false,
    },
    thirdPartyProviders: {
        enabled: true,
        _description:
            'Administration of the third parties. Accessible through the settings dropdown menu in the main navigation',
        alternateHandover: {
            enabled: false,
            _description:
                'Opens Administration of the third parties by using a new URL and targetClientId',
        },
    },
    financialHealth: {
        newSavingEnvelope: {
            enabled: false,
            _description: 'Enables the new saving envelopes feature',
        },
    },
    supportChat: {
        enabled: false,
        proactivity: {
            enabled: false,
            _description: 'Calls to fetch data about pro-active campaigns',
        },
    },
    loyaltyStars: {
        enabled: false,
    },
    adr10LegacyAccountMapping: {
        enabled: true,
    },
    retry5XXRequests: {
        enabled: false,
    },
    cobrowsing: {
        enabled: false,
    },
    CALL_PHONE_AUTHORISED_V2: {
        enabled: false,
        _description:
            'Encourage customers to call Helpdesk via the mobile George application because of already authenticated calls. Contact Area > Call Helpdesk',
    },
    sendComplaint: {
        enabled: false,
        _description: 'Enables the Send Complaint card in the Contact Area',
    },
    supportChatConversations: {
        enabled: false,
        _description: 'Support chat conversations history',
    },
    categorizationOnLogin: {
        enabled: true,
        _description:
            'Decides whether george should call categorization of transactions on login. Country may want to have it disabled because of performance issues.',
    },
    syntheticLanguages: {
        enabled: false,
        _description: 'Allow using of additional AI translated languages',
    },
    profileLoginManagement: {
        enabled: false,
        _description:
            'Decides whether to access the new "Login Management" hybrid page from the User Profile screen or to fallback the logic showing Login & Signing in the Profile menu.',
    },
    globalSseNotifications: {
        enabled: false,
        _description: 'Enables global SSE notifications subscription',
    },
    posthogTrackingWithDataConsent: {
        enabled: false,
        _description: 'Track with postHog only for those users that have data consent approved',
    },
    junior: {
        posthogTrackingEnabled: {
            _description: 'Force posthog tracking for juniors',
            enabled: false,
        },
        beancounterTrackingEnabled: {
            _description: 'Force beancounter tracking for juniors',
            enabled: false,
        },
        newTopUpLogic: {
            _description: 'Enables top up action for eligible junior accounts',
            enabled: false,
        },
    },
    removeNetbankingFromUrl: {
        enabled: false,
        _description:
            'Removes netbanking from url in all api calls. Temporary workaround for RS, it should be removed when we migrate all usages of prestoUrl to localApiBaseUrl',
    },
    fitWeb: {
        enabled: false,
        _description: 'Makes FIT available to the user',
        obligations: {
            enabled: true,
            _description: 'Show debt obligations, in case of failing third party api',
        },
        levelUp: {
            enabled: false,
            _description: 'Show level up (membership) section at FIT',
        },
        levelUpFinal: {
            enabled: false,
            _description: 'Show final version of level up (membership) section at FIT',
        },
        whatsNew: {
            enabled: false,
            _description: "Show What's New button in FIT main page header",
        },
    },
    opRpIframes: {
        enabled: false,
        _description: 'Enables session management using OIDC protocol.',
    },
    experts: {
        enabled: false,
        _description: 'Enables AI Experts Chat.',
        onboarding: {
            enabled: false,
            _description: 'Enables Onboarding Intro.',
        },
        actionsEnabled: {
            enabled: false,
            _description: 'Enables George Actions.',
        },
        mockiOSSafeArea: {
            enabled: false,
            _description: 'Enables iOS safe area insets mocking.',
        },
        generativeUI: {
            enabled: false,
            _description: 'Enables LLM generated widgets, composed of pre-built atoms.',
        },
        stopRendering: {
            enabled: false,
            _description: 'Enables the stop button to cancel an in-progress AI response.',
        },
        debug: {
            enabled: false,
            _description: 'Enables Debug Mode.',
        },
    },
    aiPlatform: {
        maintenanceInformation: {
            enabled: false,
            _description: 'Enables maintenance information screen for the AI platform (george-ai).',
        },
        pilotInformation: {
            enabled: false,
            _description: 'Enables pilot information screen for the AI platform (george-ai).',
        },
        consentScreen: {
            enabled: true,
            _description: 'Enables consent screen for the AI platform (george-ai).',
            highlights: {
                enabled: true,
                _description:
                    'Enables highlights on the consent screen for the AI platform (george-ai).',
            },
        },
    },
    luxProxy: {
        enabled: true,
        _description: 'Access lux through proxy instead of calling it directly.',
    },
    advisorAutoLogoutOverride: {
        enabled: false,
        _description: 'Overrides auto-logout timer with advisor specific timeout.',
    },
};
